import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Button } from 'antd';
import type { MenuProps } from 'antd';
import {
  HomeOutlined,
  BankOutlined,
  FileTextOutlined,
  TeamOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { useScrollToTopWindow } from '../hooks/useScrollToTop';
import { usePartnerAuthStore } from '../stores/partnerAuthStore';
import { trpc, queryClient } from '../lib/api';
import { SERVICE_TITLE } from '@shared/config/service-config';
import { useResponsive, LAYOUT_CONSTANTS } from '../hooks/useResponsive';
import { MobileDrawerMenu } from '../components/layout/MobileDrawerMenu';
import { AppBreadcrumb, BreadcrumbProvider } from '../components/Breadcrumb';
import { DevRouteInfo } from '../components/common/DevRouteInfo';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

export function PartnerLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerUser, clearAuth } = usePartnerAuthStore();
  const [collapsed, setCollapsed] = useState(false);

  const { isMobile, useMobileMenu } = useResponsive();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useScrollToTopWindow();

  const logoutMutation = trpc.partnerAuth.logout.useMutation({
    onSuccess: () => {
      queryClient.clear();
      clearAuth();
      navigate('/partner/login');
    },
  });

  const handleLogout = () => {
    const token = localStorage.getItem('partnerToken');
    if (token) {
      logoutMutation.mutate({ token });
    } else {
      clearAuth();
      navigate('/partner/login');
    }
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'ログアウト',
      onClick: handleLogout,
    },
  ];

  const sidebarItems: MenuProps['items'] = [
    {
      key: '/partner/dashboard',
      icon: <HomeOutlined />,
      label: 'ダッシュボード',
    },
    {
      key: '/partner/companies',
      icon: <BankOutlined />,
      label: '担当会社',
    },
    {
      key: '/partner/requests',
      icon: <FileTextOutlined />,
      label: '申請一覧',
    },
    {
      key: '/partner/users',
      icon: <TeamOutlined />,
      label: 'ユーザー管理',
    },
  ];

  const selectedKey = sidebarItems
    ?.find((item) => typeof item?.key === 'string' && location.pathname.startsWith(item.key))
    ?.key as string | undefined;

  const handleMenuClick = (key: string) => {
    navigate(key);
  };

  const sidebarWidth = useMobileMenu ? 0 : (collapsed ? 80 : 240);
  const contentPadding = isMobile ? LAYOUT_CONSTANTS.CONTENT_PADDING_MOBILE : 24;
  const headerHeight = isMobile ? LAYOUT_CONSTANTS.HEADER_HEIGHT_MOBILE : LAYOUT_CONSTANTS.HEADER_HEIGHT;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {useMobileMenu && (
        <MobileDrawerMenu
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          menuItems={sidebarItems}
          selectedKeys={selectedKey ? [selectedKey] : []}
          onMenuClick={handleMenuClick}
          title="パートナーポータル"
          subtitle="PARTNER PORTAL"
        />
      )}

      {!useMobileMenu && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          width={240}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
          }}
        >
          <div
            style={{
              height: '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 16px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            {!collapsed && (
              <Text strong style={{ color: 'white', fontSize: '16px' }}>
                パートナーポータル
              </Text>
            )}
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={selectedKey ? [selectedKey] : []}
            items={sidebarItems}
            onClick={({ key }) => navigate(key)}
            style={{ borderRight: 0 }}
          />
        </Sider>
      )}

      <Layout style={{ marginLeft: sidebarWidth, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            background: '#fff',
            padding: `0 ${contentPadding}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
            position: 'fixed',
            top: 0,
            right: 0,
            left: sidebarWidth,
            height: headerHeight,
            zIndex: 99,
            transition: 'left 0.2s',
          }}
        >
          <Space>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {useMobileMenu ? (
                <MenuOutlined
                  style={{ fontSize: 20, cursor: 'pointer', padding: 8 }}
                  onClick={() => setMobileDrawerOpen(true)}
                />
              ) : (
                <Button
                  type="text"
                  icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  onClick={() => setCollapsed(!collapsed)}
                  style={{
                    fontSize: '16px',
                    width: 48,
                    height: 48,
                  }}
                />
              )}
            </div>
            {!isMobile && (
              <Text strong>{SERVICE_TITLE} - パートナーポータル</Text>
            )}
          </Space>

          <Space style={{ maxWidth: 280, minWidth: 0, overflow: 'hidden' }}>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer', maxWidth: 280, minWidth: 0 }}>
                <Avatar icon={<UserOutlined />} size={isMobile ? 'default' : 'default'} />
                {!isMobile && (
                  <div
                    style={{
                      width: 200,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      lineHeight: 1.2,
                    }}
                  >
                    <div>
                      <Text
                        strong
                        ellipsis
                        style={{ display: 'block' }}
                      >
                        {partnerUser?.name || 'ゲスト'}
                      </Text>
                    </div>
                    {partnerUser?.partnerCompanyName && (
                      <div>
                        <Text
                          type="secondary"
                          style={{ fontSize: '12px' }}
                          ellipsis
                        >
                          {partnerUser.partnerCompanyName}
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            marginTop: headerHeight,
            margin: `${headerHeight + contentPadding}px ${contentPadding}px ${contentPadding}px`,
            padding: contentPadding,
            minHeight: 280,
            background: '#fff',
            borderRadius: isMobile ? 4 : 8,
          }}
        >
          <BreadcrumbProvider>
            <AppBreadcrumb layoutType="partner" />
            <Outlet />
          </BreadcrumbProvider>
        </Content>

        {/* 開発環境でのみ表示されるファイルパス情報 */}
        <DevRouteInfo />
      </Layout>
    </Layout>
  );
}
